import type { AccountTrendGranularity } from '../types/accountTrend';

function parseLocalDateKey(key: string): { y: number; m: number; d: number } {
  const parts = key.split('-');
  const rawY = Number(parts[0] ?? '');
  const rawM = Number(parts[1] ?? '');
  const rawD = Number(parts[2] ?? '');
  return {
    y: Number.isFinite(rawY) ? rawY : 1970,
    m: Number.isFinite(rawM) ? rawM : 1,
    d: Number.isFinite(rawD) ? rawD : 1
  };
}

function toLocalDateKey(y: number, m: number, d: number): string {
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function addDays(dateKey: string, delta: number): string {
  const { y, m, d } = parseLocalDateKey(dateKey);
  const utc = new Date(Date.UTC(y, m - 1, d + delta, 12, 0, 0));
  return toLocalDateKey(utc.getUTCFullYear(), utc.getUTCMonth() + 1, utc.getUTCDate());
}

function clampAnchorToToday(anchor: string, today: string): string {
  return anchor > today ? today : anchor;
}

function isoWeekday(dateKey: string): number {
  const { y, m, d } = parseLocalDateKey(dateKey);
  const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return weekday === 0 ? 7 : weekday;
}

function startOfIsoWeek(dateKey: string): string {
  return addDays(dateKey, 1 - isoWeekday(dateKey));
}

function addMonths(dateKey: string, delta: number): string {
  const { y, m, d } = parseLocalDateKey(dateKey);
  const utc = new Date(Date.UTC(y, m - 1 + delta, d, 12, 0, 0));
  return toLocalDateKey(utc.getUTCFullYear(), utc.getUTCMonth() + 1, utc.getUTCDate());
}

function addYears(dateKey: string, delta: number): string {
  const { y, m, d } = parseLocalDateKey(dateKey);
  return toLocalDateKey(y + delta, m, d);
}

export function getTodayAnchor(): string {
  const now = new Date();
  return toLocalDateKey(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

/** Align anchor to bucket start so client requests match server focus resolution. */
export function normalizeFocusAnchor(
  anchor: string,
  granularity: AccountTrendGranularity,
  today: string = getTodayAnchor()
): string {
  const clamped = clampAnchorToToday(anchor, today);
  if (granularity === 'all' || granularity === 'day') {
    return clamped;
  }
  if (granularity === 'week') {
    return startOfIsoWeek(clamped);
  }
  if (granularity === 'month') {
    const { y, m } = parseLocalDateKey(clamped);
    return toLocalDateKey(y, m, 1);
  }
  const { y } = parseLocalDateKey(clamped);
  return toLocalDateKey(y, 1, 1);
}

export function shiftFocusAnchor(
  anchor: string,
  granularity: AccountTrendGranularity,
  direction: -1 | 1,
  today: string = getTodayAnchor()
): string {
  if (granularity === 'all') {
    return anchor;
  }

  const clamped = normalizeFocusAnchor(anchor, granularity, today);
  let next: string;

  switch (granularity) {
    case 'day':
      next = addDays(clamped, direction);
      break;
    case 'week':
      next = addDays(clamped, direction * 7);
      break;
    case 'month':
      next = addMonths(clamped, direction);
      break;
    case 'year':
      next = addYears(clamped, direction);
      break;
    default:
      next = clamped;
  }

  return normalizeFocusAnchor(next, granularity, today);
}

export function focusAnchorFromBucket(
  from: string,
  granularity: AccountTrendGranularity,
  today: string = getTodayAnchor()
): string {
  return normalizeFocusAnchor(from, granularity, today);
}

/** Widget title for tasks table, e.g. "Tasks (Jun 2026)". */
export function formatTasksSectionTitle(
  focusLabel: string | undefined,
  granularity: AccountTrendGranularity
): string {
  if (!focusLabel) {
    return 'Tasks';
  }
  if (granularity === 'all') {
    return 'Tasks (all time)';
  }
  return `Tasks (${focusLabel})`;
}

/** Widget title for timesheet section, e.g. "Timesheet (Jun 2026)". */
export function formatTimesheetSectionTitle(
  focusLabel: string | undefined,
  granularity: AccountTrendGranularity
): string {
  if (!focusLabel) {
    return 'Timesheet entries';
  }
  if (granularity === 'all') {
    return 'Timesheet (all time)';
  }
  return `Timesheet (${focusLabel})`;
}

/** Widget title for users table, e.g. "Users (Jun 2026)". */
export function formatUsersSectionTitle(
  focusLabel: string | undefined,
  granularity: AccountTrendGranularity
): string {
  if (!focusLabel) {
    return 'Users';
  }
  if (granularity === 'all') {
    return 'Users (all time)';
  }
  return `Users (${focusLabel})`;
}

/** Widget title for company accounts table. */
export function formatAccountsSectionTitle(
  focusLabel: string | undefined,
  granularity: AccountTrendGranularity
): string {
  if (!focusLabel) {
    return 'Accounts';
  }
  if (granularity === 'all') {
    return 'Accounts (all time)';
  }
  return `Accounts (${focusLabel})`;
}

/** Widget title for org companies table. */
export function formatCompaniesSectionTitle(
  focusLabel: string | undefined,
  granularity: AccountTrendGranularity
): string {
  if (!focusLabel) {
    return 'Companies';
  }
  if (granularity === 'all') {
    return 'Companies (all time)';
  }
  return `Companies (${focusLabel})`;
}

/** Widget title for task contributors table. */
export function formatContributorsSectionTitle(
  focusLabel: string | undefined,
  granularity: AccountTrendGranularity
): string {
  if (!focusLabel) {
    return 'Contributors';
  }
  if (granularity === 'all') {
    return 'Contributors (all time)';
  }
  return `Contributors (${focusLabel})`;
}
